import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';
import ToastViewport from './components/common/ToastViewport';
import { useTheme } from './contexts/ThemeContext';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import AdminMeetings from './pages/Admin/Meetings';
import CreateMeeting from './pages/Admin/CreateMeeting';
import EditMeeting from './pages/Admin/EditMeeting';
import AdminSpeakers from './pages/Admin/Speakers';
import AdminUsers from './pages/Admin/Users';
import AuditLogs from './pages/Admin/AuditLogs';
import SpeakerMeetings from './pages/Speaker/Meetings';
import UserMeetings from './pages/User/Meetings';
import ConferenceRoom from './pages/Conference/Room';
import SecureMeetingJoin from './pages/Conference/SecureMeetingJoin';
import MiroTalkRoom from './pages/Conference/MiroTalkRoom';
import MeetingIframe from './pages/Conference/MeetingIframe';
import PrivateStreamEmbed from './pages/Conference/PrivateStreamEmbed';
import MeetingEnded from './pages/Conference/MeetingEnded';
import Settings from './pages/Settings/Settings';
import SuperAdminAdmins from './pages/SuperAdmin/Admins';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  }

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ returnUrl: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const DashboardLayout = ({ children, sidebarItems, pageTitle, pageDescription }) => {
  const { theme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: theme.colors.background }}>
      <Sidebar items={sidebarItems} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Header onMenuClick={() => setSidebarOpen(o => !o)} sidebarOpen={sidebarOpen} pageTitle={pageTitle} pageDescription={pageDescription} />
        <div style={{ flex: 1, overflow: 'auto', minWidth: 0, minHeight: 'calc(100vh - 60px)' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

const adminSidebarItems = [
  { label: 'Meetings', path: '/admin/meetings' },
  { label: 'Speakers', path: '/admin/speakers' },
  { label: 'Users', path: '/admin/users' },
  { label: 'Logs', path: '/admin/logs' },
  { label: 'Settings', path: '/admin/settings' },
];

const superAdminSidebarItems = [
  { label: 'Meetings', path: '/admin/meetings' },
  { label: 'Speakers', path: '/admin/speakers' },
  { label: 'Users', path: '/admin/users' },
  { label: 'Logs', path: '/admin/logs' },
  { label: 'Admins', path: '/superadmin/admins' },
  { label: 'Settings', path: '/admin/settings' },
];

const speakerSidebarItems = [
  { label: 'My Meetings', path: '/speaker/meetings' },
  { label: 'Settings', path: '/speaker/settings' },
];

const userSidebarItems = [
  { label: 'Meetings', path: '/user/meetings' },
  { label: 'Settings', path: '/user/settings' },
];

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
      <Route path="/register" element={!user ? <Register /> : <Navigate to="/" />} />
      <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/" />} />
      <Route path="/reset-password" element={!user ? <ResetPassword /> : <Navigate to="/" />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            {user?.role === 'SUPER_ADMIN' && <Navigate to="/admin/meetings" replace />}
            {user?.role === 'ADMIN' && <Navigate to="/admin/meetings" replace />}
            {user?.role === 'SPEAKER' && <Navigate to="/speaker/meetings" replace />}
            {user?.role === 'USER' && <Navigate to="/user/meetings" replace />}
          </ProtectedRoute>
        }
      />

      <Route
        path="/conference/:roomId"
        element={
          <ProtectedRoute>
            <ConferenceRoom />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/meetings"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
            <DashboardLayout sidebarItems={user?.role === 'SUPER_ADMIN' ? superAdminSidebarItems : adminSidebarItems} pageTitle="Meetings Management" pageDescription="Manage and organize your conference meetings">
              <AdminMeetings />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/meetings/create"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
            <DashboardLayout sidebarItems={user?.role === 'SUPER_ADMIN' ? superAdminSidebarItems : adminSidebarItems} pageTitle="Create New Meeting" pageDescription="Schedule a new conference meeting with details and speakers. When the meeting is created, secure join links are emailed automatically to all active users and speakers.">
              <CreateMeeting />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/meetings/:id/edit"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
            <DashboardLayout sidebarItems={user?.role === 'SUPER_ADMIN' ? superAdminSidebarItems : adminSidebarItems} pageTitle="Edit Meeting" pageDescription="Update meeting details">
              <EditMeeting />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/speakers"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
            <DashboardLayout sidebarItems={user?.role === 'SUPER_ADMIN' ? superAdminSidebarItems : adminSidebarItems} pageTitle="Speakers Management" pageDescription="Manage speakers for your conference meetings. New speakers receive an email to set their password before they can access the speaker dashboard.">
              <AdminSpeakers />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
            <DashboardLayout sidebarItems={user?.role === 'SUPER_ADMIN' ? superAdminSidebarItems : adminSidebarItems} pageTitle="Users Management" pageDescription="View onboarded users, attendance activity, and control account status.">
              <AdminUsers />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/logs"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
            <DashboardLayout sidebarItems={user?.role === 'SUPER_ADMIN' ? superAdminSidebarItems : adminSidebarItems} pageTitle="Activity Logs" pageDescription="Complete audit trail of all actions across the platform">
              <AuditLogs />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
            <DashboardLayout sidebarItems={user?.role === 'SUPER_ADMIN' ? superAdminSidebarItems : adminSidebarItems} pageTitle="Settings" pageDescription="Manage your account settings and preferences">
              <Settings />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/superadmin/admins"
        element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <DashboardLayout sidebarItems={superAdminSidebarItems} pageTitle="Admins" pageDescription="Manage platform admins and their access">
              <SuperAdminAdmins />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/speaker/meetings"
        element={
          <ProtectedRoute allowedRoles={['SPEAKER']}>
            <DashboardLayout sidebarItems={speakerSidebarItems} pageTitle="My Meetings" pageDescription="Your assigned live/upcoming sessions appear as cards. All meetings are listed in the table below.">
              <SpeakerMeetings />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/speaker/settings"
        element={
          <ProtectedRoute allowedRoles={['SPEAKER']}>
            <DashboardLayout sidebarItems={speakerSidebarItems} pageTitle="Settings" pageDescription="Manage your account settings and preferences">
              <Settings />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/user/meetings"
        element={
          <ProtectedRoute allowedRoles={['USER']}>
            <DashboardLayout sidebarItems={userSidebarItems} pageTitle="My Meetings" pageDescription="Browse and join your upcoming health conferences">
              <UserMeetings />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/user/settings"
        element={
          <ProtectedRoute allowedRoles={['USER']}>
            <DashboardLayout sidebarItems={userSidebarItems} pageTitle="Settings" pageDescription="Manage your account settings and preferences">
              <Settings />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Secure meeting join - open to unauthenticated users so email links work directly */}
      <Route
        path="/meeting/join"
        element={<SecureMeetingJoin />}
      />

      {/* Meeting iframe - embedded MiroTalk with secure token */}
      <Route
        path="/meeting/embed/:meetingId"
        element={
          <ProtectedRoute>
            <MeetingIframe />
          </ProtectedRoute>
        }
      />

      <Route
        path="/meeting/stream/:meetingId"
        element={
          <ProtectedRoute allowedRoles={['USER', 'SPEAKER', 'ADMIN', 'SUPER_ADMIN']}>
            <PrivateStreamEmbed />
          </ProtectedRoute>
        }
      />

      {/* Meeting ended - shown after user closes the meeting */}
      <Route
        path="/meeting/ended"
        element={
          <ProtectedRoute>
            <MeetingEnded />
          </ProtectedRoute>
        }
      />

      {/* Legacy embedded MiroTalk - kept for backward compatibility */}
      <Route
        path="/meeting/room/:meetingId"
        element={
          <ProtectedRoute>
            <MiroTalkRoom />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <ToastViewport />
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
